import React, { useContext } from 'react';
import {
  Card,
  Button,
  Heading,
  TextStyle,
  Badge,
  SkeletonBodyText
} from '@superops/ui-design-system';
import { useQuery } from '@apollo/client';
import { GET_PATCH_AGENT_ACTIONS } from '@nile/banyan/src/Graphql/Query/ai';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { RMMClient } from '@nile/graphql';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { useHistoricPatchStore } from '@nile/rmm/src/stores/historicPatchStore';
import { FormattedMessage } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { NoDataCard } from '@nile/components';

const PATCH_AGENT_CONSTANT = 'PATCH_AGENT';

type Props = {};
type StabilitySeverity = 'STABLE' | 'NEUTRAL' | 'CAUTION';
const severityMap: Record<StabilitySeverity, string> = {
  NEUTRAL: 'attentionLeft',
  STABLE: 'successLeft',
  CAUTION: 'criticalLeft'
};
const mapping = {
  pending: 'info',
  approved: 'success',
  rejected: 'critical',
  deferred: 'secondary'
};
const intlMapping = {
  stable_pending: {
    title: 'patch.agent.stable.pending',
    description: 'patch.agent.stable.pending.description'
  },
  caution_pending: {
    title: 'patch.agent.caution.pending',
    description: 'patch.agent.caution.pending.description'
  },
  caution_approved: {
    title: 'patch.agent.caution.approved',
    description: 'patch.agent.caution.approved.description'
  }
};
const pendiingApproveTag = {
  pending: 'admin.mspDetails.cname.status.pending',
  approved: 'patch.listView.approvedPatches'
};

const PatchAgent = (props: Props) => {
  const aiFeatureHook = AIFeaturesHook();
  const { getIsAIFeatureEnabled, getAIFeatureData } = aiFeatureHook;
  const isEnabled = getIsAIFeatureEnabled(PATCH_AGENT_CONSTANT);
  const aiFeatureData = getAIFeatureData(PATCH_AGENT_CONSTANT);
  const displayIconUrl = aiFeatureData?.displayIconUrl;
  const setHistoricPatchesEnabled = useHistoricPatchStore(
    (state) => state.setHistoricPatchesEnabled
  );

  const { data, loading, error } = useQuery(GET_PATCH_AGENT_ACTIONS, {
    client: RMMClient,
    skip: !isEnabled
  });
  const tabContext = useContext(TabContext);
  if (!isEnabled) {
    return null;
  }

  function gradiendCardInfo({
    label,
    value
  }: {
    label: string;
    value: string;
  }) {
    return (
      <div className="fx g10">
        <div className="w30">
          <TextStyle disabled weight="Regular">
            {label}
          </TextStyle>
        </div>
        <div className="wm-270">
          <TextStyle disabled weight="Regular">
            {value}
          </TextStyle>
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="mt-40 ">
        <div className="flex g40 w100 h180">
          <div className="w100 card-style br-none">
            <SkeletonBodyText lines={3} />
          </div>
          <div className="w100 card-style br-none">
            <SkeletonBodyText lines={3} />
          </div>
          <div className="w100 card-style br-none">
            <SkeletonBodyText lines={3} />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    console.error(error);
    return null;
  }
  // return null if all patch count is 0
  const isEmpty = data?.getPatchAgentActions?.every((v) => !v.patchCount);
  // return (
  //   <div className="fx col g10">
  //     <SVGIcon name="AiSparkleOutline" />
  //     <TextStyle variation="subdued" weight="Medium">
  //       <FormattedMessage id="rmm.awaiting.patches" />
  //     </TextStyle>
  //   </div>
  // );

  return (
    <div className="mt-40 fx col g10">
      <div className="fx g10 actr">
        {displayIconUrl && (
          <img
            src={displayIconUrl}
            alt="patch agent"
            loading="lazy"
            width={20}
            height={20}
            className="rounded-3px w-20 h-20"
          />
        )}
        <Heading element="h2">
          <FormattedMessage id="patch.agent" />
        </Heading>
      </div>
      {isEmpty ? (
        <div className="flex actr jctr w100 mt40 mb40">
          <NoDataCard
            imageURL={`url(${envConfig.imageRoot}svg/ai-sparkle-outline.svg)`}
            description={<FormattedMessage id="rmm.awaiting.patches" />}
            height="40px"
            width="40px"
          />
        </div>
      ) : (
        <div className="custom-fields--modern g20">
          {data?.getPatchAgentActions?.map((v) => {
            const gradientClass = severityMap[v?.stabilitySeverity];
            if (!v.patchCount) return null;
            const key =
              `${v?.stabilitySeverity}_${v?.approvalStatus}`?.toLowerCase() ||
              '';
            const {
              title = 'patch.agentt',
              description = 'patch.agent'
            } = intlMapping[key];
            const approvalStatus = v?.approvalStatus?.toLowerCase() || '';
            const approvalStatusTag = pendiingApproveTag[approvalStatus];
            return (
              <div className="m0imp w100 h180">
                <Card key={v?.viewId} padding="sizeSmall">
                  <div className="mb10 position-relative fx h-40">
                    <div className="wp-80 message-content">
                      <TextStyle weight="Medium">
                        <FormattedMessage
                          id={title}
                          values={{ count: v?.patchCount }}
                        />
                      </TextStyle>
                    </div>
                    <div className="position-absolute right-0 mr-m-20">
                      <Badge
                        size="large"
                        uppercase
                        status={mapping[approvalStatus] || 'none'}
                      >
                        <FormattedMessage id={approvalStatusTag || 'NA'} />
                      </Badge>
                    </div>
                  </div>
                  <Card
                    padding="sizeExtraSmall"
                    noShadow
                    gradientVariant={gradientClass}
                  >
                    <div className="fx col g5 h60px">
                      {gradiendCardInfo({
                        label: <FormattedMessage id="common.summary" />,
                        value: (
                          <FormattedMessage
                            id={description}
                            values={{ count: v?.patchCount }}
                          />
                        )
                      })}
                    </div>
                  </Card>
                  <div className="mt10">
                    <Button
                      onClick={() => {
                        setHistoricPatchesEnabled(true);
                        tabContext.openTab({
                          module: 'RMM_HOME',
                          url: `/rmm/patch/${v?.viewId}`,
                          data: {
                            condition: v?.condition,
                            updateData: true,
                            viewId: v?.viewId
                          }
                        });
                      }}
                    >
                      <FormattedMessage id="rmm.view.patches" />
                    </Button>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PatchAgent;
